import React from 'react';
import {
  Users,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  QrCode,
  ArrowRight,
  ShieldCheck,
  TrendingUp,
  Activity,
  Layers,
  MapPin,
  Building2,
  Film,
} from 'lucide-react';
import { DashboardStats } from '../types';

interface DashboardViewProps {
  stats: DashboardStats | null;
  onNavigateToScanner: () => void;
}

export const DashboardView: React.FC<DashboardViewProps> = ({
  stats,
  onNavigateToScanner,
}) => {
  const totalEntered = stats?.totalEntered ?? 0;
  const totalBadges = stats?.totalBadges ?? 0;
  const attendanceRate = totalBadges > 0 ? Math.round((totalEntered / totalBadges) * 100) : 0;
  const badgeTypeCounters = stats?.badgeTypeCounters || [];
  const locationCounters = stats?.locationCounters || [];
  const recentScans = stats?.recentScans || [];

  return (
    <div className="space-y-6">
      {/* Hero Stats Card */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900/90 via-slate-900/60 to-emerald-950/30 border border-white/[0.08] p-6 sm:p-8 shadow-2xl backdrop-blur-xl">
        {/* Subtle background glow */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="flex h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs font-mono font-semibold uppercase tracking-wider text-emerald-400">
                REAL-TIME GATE ATTENDANCE
              </span>
            </div>
            <h1 className="text-4xl sm:text-6xl font-black tracking-tight text-white font-mono">
              {totalEntered}
              <span className="text-lg sm:text-2xl font-normal text-slate-500 ml-2">
                / {totalBadges} ATTENDEES
              </span>
            </h1>
            <p className="text-xs sm:text-sm text-slate-400 max-w-xl">
              Strict unique attendance verified from database records. Duplicates and page refreshes do not inflate counts.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <button
              onClick={onNavigateToScanner}
              className="group flex items-center justify-center gap-2.5 px-6 py-3.5 bg-gradient-to-r from-emerald-500 to-teal-400 hover:from-emerald-400 hover:to-teal-300 text-slate-950 font-bold text-sm rounded-2xl shadow-lg shadow-emerald-500/25 transition-all duration-150 active:scale-95"
            >
              <QrCode className="w-5 h-5 text-slate-950" />
              <span>Launch QR Scanner</span>
              <ArrowRight className="w-4 h-4 text-slate-950 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>

        {/* Attendance Progress Bar */}
        <div className="relative mt-6 pt-6 border-t border-white/[0.06] space-y-2">
          <div className="flex items-center justify-between text-xs font-mono">
            <span className="text-slate-400">Overall Event Checked-in Rate</span>
            <span className="text-emerald-400 font-bold">{attendanceRate}%</span>
          </div>
          <div className="w-full h-2.5 bg-slate-950/80 rounded-full overflow-hidden p-0.5 border border-white/[0.06]">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, Math.max(0, attendanceRate))}%` }}
            />
          </div>
        </div>
      </div>

      {/* Festival Venues & Screening Halls Breakdown */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-teal-400" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-300 font-mono">
              Festival Venues &amp; Screening Halls
            </h2>
          </div>
          <span className="text-xs text-slate-500 font-mono">
            {locationCounters.length} Screening Locations
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {locationCounters.map((loc) => {
            const locName = loc.location || loc.name;
            const count = loc.count;
            const pct = totalEntered > 0 ? Math.round((count / totalEntered) * 100) : 0;

            return (
              <div
                key={locName}
                className="relative overflow-hidden rounded-2xl bg-slate-900/70 border border-teal-500/15 p-5 hover:border-teal-500/40 transition-all duration-200 group"
              >
                {/* Subtle top accent */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 to-emerald-400" />

                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-8 h-8 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center shrink-0 text-teal-400">
                      <Film className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-base text-white truncate">
                        {locName}
                      </h3>
                      <span className="text-[11px] font-mono text-slate-400">
                        Active Screening Venue
                      </span>
                    </div>
                  </div>

                  <span className="text-xs font-mono font-bold px-2 py-0.5 rounded-full bg-teal-500/10 text-teal-300 border border-teal-500/20 shrink-0">
                    {pct}% of scans
                  </span>
                </div>

                <div className="flex items-baseline justify-between pt-2 border-t border-white/[0.04]">
                  <div>
                    <span className="text-3xl font-extrabold text-white font-mono">
                      {count}
                    </span>
                    <span className="text-xs text-slate-400 font-mono ml-2">
                      attendees checked in
                    </span>
                  </div>
                  <span className="text-[11px] font-mono text-emerald-400 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Live Gate
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Dynamic Badge Types Breakdown */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-emerald-400" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-300 font-mono">
              Badge Types &amp; Department Counters
            </h2>
          </div>
          <span className="text-xs text-slate-500 font-mono">
            {badgeTypeCounters.length} Categories Active
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {badgeTypeCounters.map((bt) => {
            const count = bt.scannedCount;
            const total = bt.totalBadges;
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
            const color = bt.color || '#10b981';

            return (
              <div
                key={bt.id}
                className="relative overflow-hidden rounded-2xl bg-slate-900/70 border border-white/[0.08] p-5 hover:border-white/20 transition-all duration-200 group"
              >
                {/* Category Top Colored Indicator Bar */}
                <div
                  className="absolute top-0 left-0 right-0 h-1"
                  style={{ backgroundColor: color }}
                />

                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0 shadow-sm"
                        style={{ backgroundColor: color }}
                      />
                      <h3 className="font-bold text-base text-white truncate max-w-[150px]">
                        {bt.name}
                      </h3>
                    </div>
                    <span className="text-[11px] font-mono text-slate-400 ml-4.5">
                      CODE: {bt.code}
                    </span>
                  </div>

                  <span className="text-xs font-mono font-bold px-2 py-0.5 rounded-full bg-white/[0.05] text-slate-300 border border-white/[0.08]">
                    {pct}%
                  </span>
                </div>

                <div className="flex items-baseline justify-between pt-2">
                  <div>
                    <span className="text-3xl font-extrabold text-white font-mono">
                      {count}
                    </span>
                    <span className="text-xs text-slate-500 font-mono ml-1.5">
                      / {total} scanned
                    </span>
                  </div>
                  <span className="text-[11px] text-slate-400">
                    {total - count} remaining
                  </span>
                </div>

                <div className="w-full h-1.5 bg-slate-950 rounded-full overflow-hidden mt-3">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min(100, Math.max(0, pct))}%`,
                      backgroundColor: color,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Live Recent Scans History */}
      <div className="rounded-3xl bg-slate-900/70 border border-white/[0.08] p-5 sm:p-6 backdrop-blur-xl space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-emerald-400" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-300 font-mono">
              Live Gate Activity Feed
            </h2>
          </div>
          <span className="text-xs font-mono text-slate-500">Last 10 Scans</span>
        </div>

        {recentScans.length === 0 ? (
          <div className="py-12 text-center space-y-2">
            <div className="w-12 h-12 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mx-auto text-slate-600">
              <QrCode className="w-6 h-6" />
            </div>
            <p className="text-sm font-medium text-slate-400">No scans recorded in the database yet</p>
            <p className="text-xs text-slate-600">
              Open the scanner and scan a badge QR code to start real-time tracking
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {recentScans.map((scan) => {
              const isSuccess = scan.result === 'VALID';
              const isDuplicate = scan.result === 'ALREADY_SCANNED';

              return (
                <div
                  key={scan.id}
                  className="flex items-center justify-between gap-3 p-3.5 rounded-xl bg-slate-950/60 border border-white/[0.05] hover:border-white/10 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="shrink-0">
                      {isSuccess ? (
                        <div className="w-8 h-8 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                          <CheckCircle2 className="w-4 h-4" />
                        </div>
                      ) : isDuplicate ? (
                        <div className="w-8 h-8 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400">
                          <AlertTriangle className="w-4 h-4" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-rose-500/15 border border-rose-500/30 flex items-center justify-center text-rose-400">
                          <XCircle className="w-4 h-4" />
                        </div>
                      )}
                    </div>

                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                        <span className="font-semibold text-xs sm:text-sm text-white truncate">
                          {scan.participantName || scan.secureQrToken}
                        </span>
                        {scan.badgeTypeName && (
                          <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/[0.06] text-slate-300">
                            {scan.badgeTypeName}
                          </span>
                        )}
                        {scan.location && (
                          <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-teal-500/15 text-teal-300 border border-teal-500/30 flex items-center gap-1">
                            <MapPin className="w-2.5 h-2.5" />
                            {scan.location}
                          </span>
                        )}
                      </div>
                      <span className="text-[11px] font-mono text-slate-500">
                        {scan.badgeNumber ? `#${scan.badgeNumber} · ` : ''}
                        {scan.message || scan.result}
                      </span>
                    </div>
                  </div>

                  <div className="text-right shrink-0">
                    <span
                      className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-md border ${
                        isSuccess
                          ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                          : isDuplicate
                          ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                          : 'bg-rose-500/15 text-rose-300 border-rose-500/30'
                      }`}
                    >
                      {isSuccess ? 'VALID' : isDuplicate ? 'DUPLICATE' : 'INVALID'}
                    </span>
                    <div className="text-[10px] font-mono text-slate-500 mt-1">
                      {new Date(scan.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
